import { Typography, TextField } from '@mui/material';
import { Stack, Box } from '@mui/system';
import { FC, FormEventHandler } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from 'src/components/button';
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning.svg';
import { getTheme } from 'src/utils/get-theme';

import { useForgotPasswordSupabase } from '../hooks/use-forgot-password-supabase';

import { styles } from './styles/otp-form';

export const SupabaseForgotPasswordForm: FC<{
    email: string;
    onEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    switchToPassword: () => void;
}> = (props) => {
    const { email, onEmailChange, switchToPassword } = props;
    const { t } = useTranslation();
    const {
        mutate: forgotPassword,
        isLoading,
        error: forgotPasswordError,
        isSuccess,
    } = useForgotPasswordSupabase();
    const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();
        forgotPassword({ email });
    };
    const { theme } = getTheme();

    const getErrorMessage = (): string | null => {
        if (!forgotPasswordError) return null;

        switch (forgotPasswordError.status) {
            case 429:
                return forgotPasswordError.message;
            default:
                return t('supabase_password_login_error_generic');
        }
    };

    return (
        <Box css={styles.login}>
            <Box css={styles.loginContainer}>
                <Stack css={styles.card}>
                    <Box css={styles.headerBox}>
                        <Typography variant="h2" css={styles.headerLineHeight}>
                            {t('supabase_forgot_password')}
                        </Typography>
                        <Typography variant="body2" color={theme.palette.text.secondary}>
                            {t('supabase_forgotten_password_text')}
                        </Typography>
                    </Box>
                    {getErrorMessage() && (
                        <Stack direction="row" alignItems="center" css={styles.errorWrap} gap={1}>
                            <WarningIcon
                                width={theme.typography.h2.fontSize}
                                height={theme.typography.h2.fontSize}
                                color={theme.palette.error.main}
                            />
                            <Typography variant="body1" color={theme.palette.error.main}>
                                {getErrorMessage()}
                            </Typography>
                        </Stack>
                    )}
                    {isSuccess ? (
                        <Typography variant="body1">
                            <Trans
                                i18nKey="supabase_password_reset_email_sent"
                                values={{ email }}
                            />
                        </Typography>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <Box css={styles.inputsWrap}>
                                <TextField
                                    value={email}
                                    variant="standard"
                                    onChange={onEmailChange}
                                    type="email"
                                    name="Email"
                                    placeholder={t('supabase_email')}
                                    id="email-input"
                                    fullWidth
                                    error={Boolean(forgotPasswordError)}
                                    css={styles.inputField}
                                />
                            </Box>
                            <Button
                                color="primary"
                                type="submit"
                                id="submit-button"
                                variant="outlined"
                                fullWidth
                                loading={isLoading}
                                css={styles.button}
                            >
                                {t('supabase_password_reset_submit')}
                            </Button>
                            <Box css={styles.loginTypeSwitchWrap}>
                                <Typography
                                    variant="body2"
                                    role="button"
                                    css={styles.secondaryButton}
                                    onClick={switchToPassword}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            switchToPassword();
                                        }
                                    }}
                                    tabIndex={0}
                                >
                                    {t('supabase_back_to_login')}
                                </Typography>
                            </Box>
                        </form>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};
